import { formatMinor } from '../../shared/utils/money';
import type { EarningsSummary as EarningsSummaryType } from './earnings';

interface Props {
  summary: EarningsSummaryType;
}

function formatSettledDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function EarningsSummary({ summary }: Props) {
  const { current, previous, allTimeNet, latestSettlement, currency } = summary;

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Net earnings this month */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <p className="text-xs font-medium text-[#3F3E42] uppercase tracking-wide">
            Net earnings (this month)
          </p>
          <p className="mt-2 text-2xl font-semibold text-[#28282A]">
            {formatMinor(current.net, currency)}
          </p>
          <p className="mt-1 text-sm text-[#3F3E42]">
            {current.settlementCount} settlement{current.settlementCount !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Net earnings last month */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <p className="text-xs font-medium text-[#3F3E42] uppercase tracking-wide">
            Net earnings (last month)
          </p>
          <p className="mt-2 text-2xl font-semibold text-[#28282A]">
            {formatMinor(previous.net, currency)}
          </p>
          <p className="mt-1 text-sm text-[#3F3E42]">
            {previous.settlementCount} settlement{previous.settlementCount !== 1 ? 's' : ''}
          </p>
        </div>

        {/* All-time net */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <p className="text-xs font-medium text-[#3F3E42] uppercase tracking-wide">
            All-time net
          </p>
          <p className="mt-2 text-2xl font-semibold text-[#28282A]">
            {formatMinor(allTimeNet, currency)}
          </p>
        </div>

        {/* Most recent settlement */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <p className="text-xs font-medium text-[#3F3E42] uppercase tracking-wide">
            Most recent settlement
          </p>
          {latestSettlement ? (
            <>
              <p className="mt-2 text-2xl font-semibold text-[#28282A]">
                {formatMinor(latestSettlement.amount, latestSettlement.currency)}
              </p>
              <p className="mt-1 text-sm text-[#3F3E42]">
                {formatSettledDate(latestSettlement.settledAt)}
              </p>
            </>
          ) : (
            <p className="mt-2 text-sm text-[#3F3E42]">No settlements yet</p>
          )}
        </div>
      </div>

      <p className="mt-3 text-xs text-[#3F3E42]">
        Funds settle continuously to your bank under split-at-source — there is no scheduled batch
        payout.
      </p>
    </div>
  );
}
